import React, { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useClient } from '@/contexts/ClientContext';

interface ClientAwareRedirectProps {
  children: React.ReactNode;
}

export const ClientAwareRedirect: React.FC<ClientAwareRedirectProps> = ({ children }) => {
  const [location, navigate] = useLocation();
  const { selectedClient } = useClient();

  useEffect(() => {
    // If we have a selected client and we're on a non-client-aware route, redirect
    if (selectedClient && !location.startsWith('/clients/')) {
      // List of routes that should be client-aware
      const clientAwareRoutes = [
        '/sql/management',
        '/sql/configurations',
        '/sql/database-design',
        '/sql/security',
        '/sql/performance',
        '/sql/error-logs',
        '/sql/alwayson',
        '/sql/maintenance',
        '/sql/properties',
        '/sql/excellence',
        '/togaf',
        '/zachman',
        '/safe',
        '/archimate',
        '/wsr',
        '/discovery',
        '/findings',
        '/reports',
        '/stress-testing'
      ];

      const shouldRedirect = clientAwareRoutes.some(route => 
        location === route || location.startsWith(route + '/')
      );

      if (shouldRedirect) {
        const clientAwarePath = `/clients/${selectedClient.code}${location}`;
        console.log(`Client-aware redirect: ${location} → ${clientAwarePath}`);
        
        // Show a brief notification about the redirect
        const notification = document.createElement('div');
        notification.innerHTML = `
          <div style="
            position: fixed; 
            top: 20px; 
            right: 20px; 
            background: #3b82f6; 
            color: white; 
            padding: 12px 16px; 
            border-radius: 8px; 
            font-size: 14px; 
            z-index: 10000;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          ">
            Switched to ${selectedClient.name} context
          </div>
        `;
        document.body.appendChild(notification);
        
        // Remove notification after 3 seconds
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 3000);
        
        navigate(clientAwarePath, { replace: true });
      }
    }
  }, [location, selectedClient, navigate]);

  return <>{children}</>;
};